import polygonClipping from 'polygon-clipping'
import type { Pt2 } from './groundWash/mercator'

// polygon-clipping only ships a default export at runtime (its .d.ts declares
// named exports, which typechecks under `moduleResolution: bundler` but breaks
// the production Rollup build) — see groundWash/filledWash.ts and
// polygonUnion.ts for the same import shape.

export type { Pt2 }

/** Wire coordinates are rounded to this many decimals by {@link roundCoord}.
 *  6 decimal degrees is about 11 cm — comfortably finer than
 *  anything the print pipeline draws — so rounding here trims payload weight
 *  (the reported bug was ~40 000 pairs carrying 14-15 decimals) without a
 *  reader ever seeing the difference. */
const DEFAULT_ROUND_DECIMALS = 6

/** Millimetres per inch, the bridge between DPI and the metric tolerance below. */
const MM_PER_INCH = 25.4

/** The print server rasterises at 300 DPI. */
const PRINT_DPI = 300

/** Default Douglas-Peucker tolerance in output pixels: half the finest dot the
 *  raster can place. A reader cannot see a deviation smaller than half a
 *  pixel, so simplifying to that tolerance changes nothing visible while
 *  cutting the geometry that made the reported request 1.6 MB. */
const DEFAULT_TOLERANCE_PIXELS = 0.5

/**
 * Round a coordinate value (half-up) to the wire's precision — see
 * {@link DEFAULT_ROUND_DECIMALS}. Apply this last, after clipping and
 * simplification: those run on full precision so a rounded-then-clipped point
 * doesn't accumulate the rounding error twice.
 *
 * Deliberately UNARY, with the precision baked in rather than offered as a
 * second parameter. A `(v, decimals?)` signature is one an array callback can
 * fill in by accident: `pair.map(roundCoord)` passes the INDEX as `decimals`,
 * so the longitude rounds to whole degrees and the latitude to one decimal —
 * a coordinate silently moved tens of kilometres, in code that reads
 * correctly. Nothing in this pipeline wants another precision, so the
 * parameter only ever cost.
 */
export function roundCoord(v: number): number {
  const factor = 10 ** DEFAULT_ROUND_DECIMALS
  return Math.round(v * factor) / factor
}

/**
 * The Douglas-Peucker tolerance, in ground metres, for a sheet printed at
 * `1:scale`: `pixels` output pixels at {@link PRINT_DPI}, converted through
 * the paper-to-ground ratio the scale defines. Half a pixel (the default) is
 * the finest positional difference the raster can actually show, so
 * simplifying to it is lossless to the eye — 0.42 m at 1:10 000, ~2.12 m at
 * 1:50 000.
 */
export function printPixelToleranceMetres(scale: number, pixels = DEFAULT_TOLERANCE_PIXELS): number {
  const mmOnPaper = pixels * (MM_PER_INCH / PRINT_DPI)
  const mmOnGround = mmOnPaper * scale
  return mmOnGround / 1000
}

function sqDist([ax, ay]: Pt2, [bx, by]: Pt2): number {
  const dx = ax - bx
  const dy = ay - by
  return dx * dx + dy * dy
}

/** Perpendicular distance from `p` to the infinite line through `a` and `b` —
 *  the measure Douglas-Peucker itself uses to pick the farthest point, and
 *  the one the closed-ring fallback below reuses to pick a third point when
 *  the ring would otherwise collapse to a line. */
function perpendicularDistance(p: Pt2, a: Pt2, b: Pt2): number {
  const [px, py] = p
  const [ax, ay] = a
  const [bx, by] = b
  const dx = bx - ax
  const dy = by - ay
  const lenSq = dx * dx + dy * dy
  if (lenSq === 0) return Math.hypot(px - ax, py - ay)
  const cross = dx * (py - ay) - dy * (px - ax)
  return Math.abs(cross) / Math.sqrt(lenSq)
}

/** Standard iterative Douglas-Peucker over an open chain — both endpoints are
 *  always kept. An explicit stack rather than recursion, because a real
 *  parcel outline can carry tens of thousands of vertices and a recursive
 *  version would blow the call stack on a nearly-straight run of them. */
function douglasPeucker(pts: Pt2[], tolerance: number): Pt2[] {
  if (pts.length <= 2) return pts.slice()
  const keep = new Uint8Array(pts.length)
  keep[0] = 1
  keep[pts.length - 1] = 1
  const stack: [number, number][] = [[0, pts.length - 1]]
  while (stack.length > 0) {
    const [start, end] = stack.pop()!
    let maxDist = -1
    let maxIdx = -1
    for (let i = start + 1; i < end; i++) {
      const d = perpendicularDistance(pts[i], pts[start], pts[end])
      if (d > maxDist) {
        maxDist = d
        maxIdx = i
      }
    }
    if (maxDist > tolerance) {
      keep[maxIdx] = 1
      stack.push([start, maxIdx], [maxIdx, end])
    }
  }
  return pts.filter((_, i) => keep[i] === 1)
}

function farthestIndexFrom(pts: Pt2[], fromIdx: number): number {
  let bestIdx = -1
  let bestDist = -1
  const from = pts[fromIdx]
  for (let i = 0; i < pts.length; i++) {
    if (i === fromIdx) continue
    const d = sqDist(pts[i], from)
    if (d > bestDist) {
      bestDist = d
      bestIdx = i
    }
  }
  return bestIdx
}

function countDistinct(pts: Pt2[]): number {
  const seen = new Set<string>()
  for (const [x, y] of pts) seen.add(`${x},${y}`)
  return seen.size
}

/**
 * A closed ring must keep an area, not collapse to a line, so a two-point
 * result is not an acceptable simplification even when DP's tolerance says
 * every interior point is negligible. Restore whichever original point
 * deviates most from the line through the two survivors — the same measure
 * DP itself ranks candidates by — so the ring keeps some real footprint
 * instead of an arbitrary one.
 *
 * `original` is guaranteed by the caller to carry three distinct points, so
 * this is always restoring a point the input really had. Without that
 * guarantee the same code would answer a one-point input with three copies of
 * that point, which is a ring invented here rather than one preserved.
 */
function ensureRingHasThreePoints(ring: Pt2[], original: Pt2[]): Pt2[] {
  if (countDistinct(ring) >= 3) return ring
  const a = ring[0]
  const b = ring.length >= 2 ? ring[1] : ring[0]
  let bestIdx = -1
  let bestDist = -1
  for (let i = 0; i < original.length; i++) {
    const d = perpendicularDistance(original[i], a, b)
    if (d > bestDist) {
      bestDist = d
      bestIdx = i
    }
  }
  return bestIdx >= 0 ? [a, b, original[bestIdx]] : ring
}

/**
 * Douglas-Peucker simplification. `closed` treats `pts` as a ring stored open
 * (first point not repeated): it is split at the point farthest from `pts[0]`
 * — splitting at the ring's widest diameter rather than at index 0 keeps both
 * halves representative, where an arbitrary split risks one half being a
 * thin sliver DP erases entirely — each half is simplified independently and
 * the results merged back into one ring, then topped up to at least three
 * distinct points if the merge left fewer (see
 * {@link ensureRingHasThreePoints}). An open polyline keeps at least its two
 * endpoints.
 *
 * A closed input that carries fewer than three DISTINCT points is returned
 * deduped — one point for an all-identical ring, two for a there-and-back —
 * never padded out to three. Such an input encloses nothing, and no
 * simplification can change that; answering it with three copies of one
 * coordinate would invent a ring the caller never had and put a wire polygon
 * with no area on the paper.
 */
export function simplifyPolyline(pts: Pt2[], tolerance: number, closed: boolean): Pt2[] {
  if (!closed) {
    if (pts.length < 2) return pts.slice()
    return douglasPeucker(pts, tolerance)
  }

  // Deduped up front, which is also what lets the top-up below assume its
  // `original` has a third distinct point to find.
  const ring = dedupeConsecutive(pts, true)
  if (countDistinct(ring) < 3) return ring

  const anchor = farthestIndexFrom(ring, 0)
  const chainA = ring.slice(0, anchor + 1)
  const chainB = [...ring.slice(anchor), ring[0]]
  const simplifiedA = douglasPeucker(chainA, tolerance)
  const simplifiedB = douglasPeucker(chainB, tolerance)
  const merged = [...simplifiedA.slice(0, -1), ...simplifiedB.slice(0, -1)]

  return ensureRingHasThreePoints(merged, ring)
}

export interface Rect {
  minX: number
  minY: number
  maxX: number
  maxY: number
}

/** Collapse consecutive identical points to one, and, for a closed ring,
 *  drop a trailing point that duplicates the first (a ring is stored open —
 *  see the module doc — so a closing duplicate is itself a defect to strip,
 *  not a real segment). Real ground geometry carries these regularly
 *  (`polygon-clipping` output, hand-drawn rings), and a duplicate is not a
 *  span to clip — it must vanish before segments are built at all, rather
 *  than being clipped to zero length and read as a run boundary. */
function dedupeConsecutive(pts: Pt2[], closed: boolean): Pt2[] {
  const out: Pt2[] = []
  for (const p of pts) {
    const last = out[out.length - 1]
    if (!last || last[0] !== p[0] || last[1] !== p[1]) out.push(p)
  }
  if (closed && out.length > 1) {
    const first = out[0]
    const last = out[out.length - 1]
    if (first[0] === last[0] && first[1] === last[1]) out.pop()
  }
  return out
}

/**
 * Liang-Barsky clip of one segment against `rect`: the parametric line
 * `p0 + t*(p1-p0)` is walked against the four half-planes the rectangle's
 * sides define, narrowing `[tMin, tMax]` until it either vanishes (segment
 * misses the rectangle) or bounds the surviving sub-segment.
 */
function clipSegment(p0: Pt2, p1: Pt2, rect: Rect): [Pt2, Pt2] | null {
  const dx = p1[0] - p0[0]
  const dy = p1[1] - p0[1]
  let tMin = 0
  let tMax = 1
  const edges: [number, number][] = [
    [-dx, p0[0] - rect.minX],
    [dx, rect.maxX - p0[0]],
    [-dy, p0[1] - rect.minY],
    [dy, rect.maxY - p0[1]],
  ]
  for (const [p, q] of edges) {
    if (p === 0) {
      if (q < 0) return null // parallel to this edge and outside it
      continue
    }
    const t = q / p
    if (p < 0) {
      if (t > tMax) return null
      if (t > tMin) tMin = t
    } else {
      if (t < tMin) return null
      if (t < tMax) tMax = t
    }
  }
  if (tMin > tMax) return null
  const a: Pt2 = [p0[0] + tMin * dx, p0[1] + tMin * dy]
  const b: Pt2 = [p0[0] + tMax * dx, p0[1] + tMax * dy]
  // The caller dedupes consecutive input points (see dedupeConsecutive), so a
  // zero-length result here is a genuine corner or edge touch, not a
  // zero-length input segment — either way it carries no direction, and
  // treating it as "kept" would seed or extend a piece with a duplicated
  // point instead of a real traversed span.
  if (a[0] === b[0] && a[1] === b[1]) return null
  return [a, b]
}

function approxEqual([ax, ay]: Pt2, [bx, by]: Pt2, eps: number): boolean {
  return Math.abs(ax - bx) <= eps && Math.abs(ay - by) <= eps
}

/** Two adjacent original segments can both survive clipping without their
 *  clipped pieces actually touching: segment i can exit the rectangle before
 *  reaching the shared vertex, and segment i+1 can re-enter it only partway
 *  through — the shared vertex itself sits outside. `eps` (relative to the
 *  rect size, since the exit and re-entry points come from two independent
 *  Liang-Barsky computations rather than being the same value) absorbs that
 *  floating-point slop while still telling a real gap from a rounding wobble. */
function clipEpsilon(rect: Rect): number {
  return 1e-9 * Math.max(rect.maxX - rect.minX, rect.maxY - rect.minY)
}

/** Group a linear sequence of per-segment clip results into pieces: a kept
 *  segment extends the current piece only when its clipped start actually
 *  meets the piece's last point — two segments can both survive clipping
 *  without being contiguous (see {@link clipEpsilon}) — otherwise it starts a
 *  new one; a dropped segment (null) always ends the current piece. */
function linearRuns(segClips: ([Pt2, Pt2] | null)[], eps: number): Pt2[][] {
  const pieces: Pt2[][] = []
  let current: Pt2[] | null = null
  for (const seg of segClips) {
    if (!seg) {
      if (current) pieces.push(current)
      current = null
      continue
    }
    if (current && approxEqual(current[current.length - 1], seg[0], eps)) {
      current.push(seg[1])
    } else {
      if (current) pieces.push(current)
      current = [seg[0], seg[1]]
    }
  }
  if (current) pieces.push(current)
  return pieces
}

/** Same grouping as {@link linearRuns}, but circular: a ring's closing
 *  segment sits next to its first segment, so a run that straddles the
 *  array boundary must not be reported as two pieces. */
function circularRuns(segClips: ([Pt2, Pt2] | null)[], eps: number): Pt2[][] {
  const n = segClips.length
  // Is segClips[i] contiguous with its circular predecessor? False whenever
  // either is null, or both are kept but don't actually meet (see
  // clipEpsilon) — either way, i starts a new run.
  const contiguous = (i: number): boolean => {
    const prev = segClips[(i - 1 + n) % n]
    const cur = segClips[i]
    return prev !== null && cur !== null && approxEqual(prev[1], cur[0], eps)
  }
  if (segClips.every((s) => s !== null) && segClips.every((_, i) => contiguous(i))) {
    // Nothing was dropped and every join actually meets: the whole ring
    // survived as one closed loop. Build it from every segment's start plus
    // the final wraparound endpoint, then drop that duplicated closing point
    // — this module's open-ring convention.
    const chain: Pt2[] = [segClips[0]![0]]
    for (const seg of segClips) chain.push(seg![1])
    return [chain.slice(0, -1)]
  }
  if (segClips.every((s) => s === null)) return []
  // Rotate to start right after a genuine run boundary — a drop, or a kept
  // segment that doesn't meet its predecessor — so every surviving run is
  // contiguous within the rotated array and none straddles the wraparound
  // seam. linearRuns' own contiguity check then finds every internal
  // boundary the same way it would for an open polyline.
  const startIdx = segClips.findIndex((s, i) => s !== null && !contiguous(i))
  const rotated = Array.from({ length: n }, (_, i) => segClips[(startIdx + i) % n])
  return linearRuns(rotated, eps)
}

/**
 * Liang-Barsky clip of a polyline (or, with `closed`, a ring plus its
 * closing segment) against `rect`, per segment. Consecutive kept segments
 * merge into one piece, so the result is zero or more open polylines: a
 * fully-outside input yields `[]`, a fully-inside one yields the input
 * unchanged (as one piece; a closed ring loses only its duplicated closing
 * point), and each cut endpoint lands exactly on a rectangle edge.
 */
export function clipPolylineToRect(pts: Pt2[], rect: Rect, closed: boolean): Pt2[][] {
  // A consecutive duplicate vertex is ordinary input here (polygon-clipping
  // output and hand-drawn rings both produce them), not a real zero-length
  // span to clip. Removing it up front means clipSegment only ever sees a
  // genuine segment, so its zero-length `null` fires solely for an actual
  // corner/edge touch rather than also splitting a run at an interior
  // duplicate.
  const deduped = dedupeConsecutive(pts, closed)
  if (deduped.length < 2) return []
  const n = deduped.length
  const segCount = closed ? n : n - 1
  const segClips: ([Pt2, Pt2] | null)[] = []
  for (let i = 0; i < segCount; i++) {
    segClips.push(clipSegment(deduped[i], deduped[(i + 1) % n], rect))
  }
  const eps = clipEpsilon(rect)
  const pieces = closed ? circularRuns(segClips, eps) : linearRuns(segClips, eps)
  // Belt-and-suspenders alongside clipSegment's own zero-length check: a
  // piece built entirely from touching points has no direction and is not a
  // real polyline piece, whatever combination of runs produced it.
  return pieces.filter((piece) => countDistinct(piece) >= 2)
}

function closeRing(ring: Pt2[]): Pt2[] {
  const [fx, fy] = ring[0]
  const [lx, ly] = ring[ring.length - 1]
  return fx === lx && fy === ly ? ring : [...ring, [fx, fy]]
}

function openRing(ring: Pt2[]): Pt2[] {
  if (ring.length <= 1) return ring
  const [fx, fy] = ring[0]
  const [lx, ly] = ring[ring.length - 1]
  return fx === lx && fy === ly ? ring.slice(0, -1) : ring
}

function rectRing(rect: Rect): Pt2[] {
  const { minX, minY, maxX, maxY } = rect
  return [
    [minX, minY],
    [maxX, minY],
    [maxX, maxY],
    [minX, maxY],
  ]
}

/**
 * Intersection of one polygon (outer ring then holes, open rings) with
 * `rect`, via `polygon-clipping` — the same library and closed/open ring
 * conversion `groundWash/clipperPolygons.ts` uses for the wash, just against
 * a rectangle instead of another area. A polygon that straddles the
 * rectangle can split into several disjoint pieces, so the result is a list
 * of polygons rather than one; a polygon wholly outside `rect` yields `[]`.
 *
 * A thrown clip (self-intersecting or otherwise degenerate input) yields no
 * geometry rather than a broken print request — the same trade
 * `filledWashPolygons` makes.
 */
export function clipPolygonToRect(poly: Pt2[][], rect: Rect): Pt2[][][] {
  if (poly.length === 0) return []
  try {
    const subject = poly.map(closeRing)
    const clip = [closeRing(rectRing(rect))]
    const result = polygonClipping.intersection(subject, clip)
    return result.map((p) => p.map(openRing))
  } catch {
    return []
  }
}

/**
 * The sheet's clip rectangle: `center` plus half of `extentM` on each axis,
 * expanded by `marginM` on every side. `marginM` already carries whatever the
 * caller decided the margin should be (a fraction of the sheet plus the
 * widest stroke half-width, per the print request design) — this function
 * only does the rectangle arithmetic.
 */
export function sheetRect(center: Pt2, extentM: { w: number; h: number }, marginM: number): Rect {
  const halfW = extentM.w / 2 + marginM
  const halfH = extentM.h / 2 + marginM
  return {
    minX: center[0] - halfW,
    minY: center[1] - halfH,
    maxX: center[0] + halfW,
    maxY: center[1] + halfH,
  }
}
